import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useDisclosure } from "@mantine/hooks";
import FolderService from "../services/FolderService";
import FilterService from "../services/FilterService";
import { AuthContext } from "./AuthContext";
import { FolderFilter, FolderFilters } from "../models/FolderFilters";
import CreateFolderModal from "../components/CreateFolderModal";
import RenameFolderModal from "../components/RenameFolderModal";
import DeleteFolderModal from "../components/DeleteFolderModal";
import MoveFolderModal from "../components/MoveFolderModal";
import { UserLevels } from "../utils/constants";

export function searchFolders(query, folders) {
  if (!query || query.trim() === "") {
    return [];
  }

  const searchTerms = query
    .trim()
    .toLowerCase()
    .split(/[\s\-_]+/)
    .filter((term) => term.length >= 2);

  if (searchTerms.length === 0) {
    return [];
  }

  return folders.filter((folder) => {
    const label = folder.label.toLowerCase();
    return searchTerms.every((term) => label.includes(term));
  });
}

export const FoldersContext = createContext({
  folders: [],
  flattenFolders: () => [],
  flatFolders: [],
  sortedFolders: [],
  loading: false,
  searching: false,
  setSearching: () => {},
  rootId: null,
  createFolder: () => {},
  renameFolder: () => {},
  deleteFolder: () => {},
  moveFolder: () => {},
  openCreate: () => {},
  canCreateFolder: false,
  openRename: () => {},
  openDelete: () => {},
  openMove: () => {},
  setFolderToModify: () => {},
  searchFolders: () => [],
  sortBy: "alphabetical",
  setSortBy: () => {},
  folderIdMap: {},
  viewingFolder: null,
  setViewingFolder: () => {},
  expandedCheckedNodes: false,
  setExpandedCheckedNodes: () => {},
  collapseAll: false,
  setCollapseAll: () => {},
  handleExpandChecked: () => {},
  handleCollapseAll: () => {},
  folderFilters: new FolderFilters(),
  folderFiltersLoading: false,
  setFolderFilters: () => {},
  updateFolderFilters: () => {},
  toggleFolderFilter: () => {},
  checkAllFolderFilters: () => {},
  uncheckAllFolderFilters: () => {},
});

function flattenFolders(nodes) {
  const result = [];
  const traverse = (nodes) => {
    nodes.forEach((node) => {
      result.push(node);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  };
  traverse(nodes);
  return result;
}

function sortTree(nodes, sortBy) {
  const sortedNodes = [...nodes].sort((a, b) => {
    if (sortBy === "alphabetical") {
      return a.label.localeCompare(b.label);
    } else if (sortBy === "createdDate") {
      return new Date(b.createdDate) - new Date(a.createdDate);
    }
    return 0;
  });
  return sortedNodes.map((node) => ({
    ...node,
    children: node.children ? sortTree(node.children, sortBy) : [],
  }));
}

export function FoldersProvider({ children }) {
  const { dataScopeOrgId, user } = useContext(AuthContext);
  const canCreateFolder = user?.userLevel !== UserLevels.UPLOAD_ONLY;
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [folderToModify, setFolderToModify] = useState(null);
  const [createModal, { open: openCreateModal, close: closeCreate }] =
    useDisclosure(false);
  const openCreate = () => {
    if (canCreateFolder) openCreateModal();
  };
  const [renameModal, { open: openRename, close: closeRename }] =
    useDisclosure(false);
  const [deleteModal, { open: openDelete, close: closeDelete }] =
    useDisclosure(false);
  const [moveModal, { open: openMove, close: closeMove }] =
    useDisclosure(false);
  const [sortBy, setSortBy] = useState("alphabetical");
  const [viewingFolder, setViewingFolder] = useState(null);

  const [expandedCheckedNodes, setExpandedCheckedNodes] = useState(false);
  const [collapseAll, setCollapseAll] = useState(false);

  const [folderFilters, setFolderFilters] = useState(new FolderFilters());
  const [folderFiltersLoading, setFolderFiltersLoading] = useState(true);

  // Org whose folders AND filters are both loaded; stale data reads as a mismatch.
  const [loadedOrgId, setLoadedOrgId] = useState(null);

  const rootId = useMemo(() => folders[0]?.value, [folders]);
  const flatFolders = useMemo(() => flattenFolders(folders), [folders]);

  const fetchFolders = async (silent = false) => {
    if (!dataScopeOrgId) return;
    try {
      setLoading(!silent);
      const treeData = await FolderService.getFolders(dataScopeOrgId);
      setFolders(treeData || []);
    } catch (error) {
      console.error("Folders Context - Error fetching folders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderFilters = async (silent = false) => {
    if (!dataScopeOrgId) return;
    try {
      if (!silent) {
        setFolderFiltersLoading(true);
      }
      const { folderFilters: serverFolderFilters } =
        await FilterService.getFilters(dataScopeOrgId);
      setFolderFilters(
        new FolderFilters(serverFolderFilters.map((item) => ({ id: item.id })))
      );
    } catch (error) {
      console.error("Error fetching folder filters:", error);
    } finally {
      if (!silent) {
        setFolderFiltersLoading(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoadedOrgId(null);
    if (!dataScopeOrgId) return;
    (async () => {
      await Promise.all([fetchFolders(), fetchFolderFilters()]);
      if (!cancelled) setLoadedOrgId(dataScopeOrgId);
    })();
    return () => {
      cancelled = true;
    };
  }, [dataScopeOrgId]);

  const folderIdMap = useMemo(() => {
    const map = {};
    flattenFolders(folders).forEach((folder) => {
      map[folder.value] = folder.label;
    });
    return map;
  }, [folders]);

  const sortedFolders = useMemo(
    () => sortTree(folders, sortBy),
    [folders, sortBy]
  );

  const createFolder = async (folderData) => {
    try {
      await FolderService.createFolder(folderData);
      closeCreate();
      fetchFolders(true);
      fetchFolderFilters(true);
    } catch (error) {
      console.error("Folders Context - Error creating folder:", error);
    }
  };

  const renameFolder = async (folderId, newName) => {
    try {
      await FolderService.renameFolder(folderId, newName);
      closeRename();
      fetchFolders(true);
    } catch (error) {
      console.error("Folders Context - Error renaming folder:", error);
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      await FolderService.deleteFolder(folderId);
      closeDelete();
      fetchFolders(true);
    } catch (error) {
      console.error("Folders Context - Error deleting folder:", error);
    }
  };

  const moveFolder = async (folderId, newParentId) => {
    try {
      await FolderService.moveFolder(folderId, newParentId);
      closeMove();
      fetchFolders(true);
    } catch (error) {
      console.error("Folders Context - Error moving folder:", error);
    }
  };

  const handleExpandChecked = () => {
    setCollapseAll(false);
    setExpandedCheckedNodes(true);
  };

  const handleCollapseAll = () => {
    setExpandedCheckedNodes(false);
    setCollapseAll(true);
  };

  const updateFolderFilters = async (newFolderFiltersObj, silent = false) => {
    try {
      if (!silent) {
        setFolderFiltersLoading(true);
      }
      const { folderFilters: updatedFolderFilters } =
        await FilterService.updateFolderFilters(
          dataScopeOrgId,
          newFolderFiltersObj,
          folders,
          silent
        );
      setFolderFilters(
        new FolderFilters(updatedFolderFilters.map((item) => ({ id: item.id })))
      );
    } catch (error) {
      console.error("Error updating folder filters:", error);
    } finally {
      if (!silent) {
        setFolderFiltersLoading(false);
      }
    }
  };

  const getAllLeafIdsFromTree = (nodes) => {
    let leafIds = [];
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        leafIds = leafIds.concat(getAllLeafIdsFromTree(node.children));
      } else {
        leafIds.push(node.value);
      }
    });
    return leafIds;
  };

  const checkAllFolderFilters = async () => {
    if (!folders || folders.length === 0) return;
    const node = findNodeById(folders, rootId);
    if (!node) return;

    const leafIds = getAllLeafIdsFromTree([node]).map(String);

    const current = folderFilters.folderFilters.map((f) => String(f.id));
    const currentSet = new Set(current);

    const allSelected = leafIds.every((lid) => currentSet.has(lid));

    let newLeafIds;
    if (allSelected) {
      return;
    } else {
      newLeafIds = Array.from(new Set([...current, ...leafIds]));
    }

    const newFolderFiltersObj = new FolderFilters(
      newLeafIds.map((id) => new FolderFilter(Number(id)))
    );
    setFolderFilters(newFolderFiltersObj);

    try {
      await updateFolderFilters(newFolderFiltersObj, true);
    } catch (err) {
      console.error("Error in updateFolderFilters:", err);
    }
  };

  const uncheckAllFolderFilters = async () => {
    const node = findNodeById(folders, rootId);
    if (!node) return;

    const leafIds = getAllLeafIdsFromTree([node]).map(String);
    const current = folderFilters.folderFilters.map((f) => String(f.id));
    let newLeafIds = current.filter((id) => !leafIds.includes(id));

    const newFolderFiltersObj = new FolderFilters(
      newLeafIds.map((id) => new FolderFilter(Number(id)))
    );
    setFolderFilters(newFolderFiltersObj);

    try {
      await updateFolderFilters(newFolderFiltersObj, true);
    } catch (err) {
      console.error("Error in uncheckAllFolderFilters:", err);
    }
  };

  function findNodeById(nodes, id) {
    for (const node of nodes) {
      if (String(node.value) === String(id)) return node;
      if (node.children?.length) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  const toggleFolderFilter = async (folderId) => {
    const node = findNodeById(folders, folderId);
    if (!node) return;

    const leafIds = getAllLeafIdsFromTree([node]).map(String);

    const current = folderFilters.folderFilters.map((f) => String(f.id));
    const currentSet = new Set(current);

    const allSelected = leafIds.every((lid) => currentSet.has(lid));

    let newLeafIds;
    if (allSelected) {
      newLeafIds = current.filter((id) => !leafIds.includes(id));
    } else {
      newLeafIds = Array.from(new Set([...current, ...leafIds]));
    }

    const newFolderFiltersObj = new FolderFilters(
      newLeafIds.map((id) => new FolderFilter(Number(id)))
    );
    setFolderFilters(newFolderFiltersObj);

    try {
      await updateFolderFilters(newFolderFiltersObj, true);
    } catch (err) {
      console.error("Error in toggleFolderFilter:", err);
    }
  };

  return (
    <FoldersContext.Provider
      value={{
        folders,
        setFolders,
        flattenFolders,
        flatFolders,
        sortedFolders,
        loading,
        searching,
        setSearching,
        rootId,
        createFolder,
        renameFolder,
        deleteFolder,
        moveFolder,
        openCreate,
        canCreateFolder,
        openRename,
        openDelete,
        openMove,
        setFolderToModify,
        searchFolders,
        sortBy,
        setSortBy,
        folderIdMap,
        viewingFolder,
        setViewingFolder,
        expandedCheckedNodes,
        setExpandedCheckedNodes,
        collapseAll,
        setCollapseAll,
        handleExpandChecked,
        handleCollapseAll,
        folderFilters,
        folderFiltersLoading,
        loadedOrgId,
        setFolderFilters,
        updateFolderFilters,
        toggleFolderFilter,
        checkAllFolderFilters,
        uncheckAllFolderFilters,
      }}
    >
      {children}
      <CreateFolderModal
        opened={createModal}
        onClose={closeCreate}
        parent={folderToModify}
      />
      <RenameFolderModal
        opened={renameModal}
        onClose={closeRename}
        folder={folderToModify}
      />
      <DeleteFolderModal
        opened={deleteModal}
        onClose={closeDelete}
        folder={folderToModify}
      />
      <MoveFolderModal
        opened={moveModal}
        onClose={closeMove}
        folder={folderToModify}
      />
    </FoldersContext.Provider>
  );
}
